import React, { CSSProperties } from "react";
import ModalManager from "../managers/modalManager";
import graphics from "../helpers/graphics";
import ControllableObject from "./controllableObject";
import SimManager from "../managers/simManager";
import ModalJSXElement from "../helpers/modal";
import "./styles.scss";
import { InteractableButtons } from "./interactableButtons";
import { Maybe, UserInput, Vector3, Condition } from "../types";
import { Label } from "../types/ObjectDefinitionTypes";
import { SceneItemProps } from "../types/SceneTypes";
import SceneItem from "./sceneObject";
import CustomObject from "./customObject";
import { ObjectState } from "../types/ObjectDefinitionTypes";
import { calculateXOffset, calculateYOffset } from "../helpers/responsiveness";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { MathJax } from "better-react-mathjax";

export interface IOProps extends SceneItemProps {
  id: string;
  modal: string;
  states: any;
  onDropItems: any;
  onclick: any;
  defaultState: any;
  defaultUserInput: any;
  position: any;
  userScale: number;
  sceneScale: number;
  text: any;
}

class InteractableObject extends SceneItem {
  private modalName: string;
  private states: ObjectState[];
  private onClickStates: any;
  private onDropItems: any;
  // eslint-disable-next-line
  private singleButtonBackground: Maybe<JSX.Element>;
  private imageSrc: string;
  private modals: Array<{
    // eslint-disable-next-line
    element: JSX.Element;
    ref: ModalJSXElement | null;
  }> = [];
  // eslint-disable-next-line
  private buttonsBackground3Buttons: Maybe<JSX.Element>;
  // eslint-disable-next-line
  private buttonsBackground2Buttons: Maybe<JSX.Element>;
  // eslint-disable-next-line
  private ejectButton: Maybe<JSX.Element>;
  // eslint-disable-next-line
  private optionsButton: Maybe<JSX.Element>;
  // eslint-disable-next-line
  private glossaryButton: Maybe<JSX.Element>;
  private userInput: any;

  constructor(props: any) {
    super(props);
    this.modalName = props.modal as string;
    if (this.modalName != "") {
      let ref: ModalJSXElement | null = null;
      this.modals.push({
        // eslint-disable-next-line
        element: (
          <ModalJSXElement
            key={this.modalName}
            ref={(r) => (ref = r)}
            modal={this.modalName}
          />
        ),
        ref: ref,
      });
    }
    if (this.props.modals) {
      this.props.modals.forEach((modal) => {
        let ref: ModalJSXElement | null = null;
        this.modals.push({
          // eslint-disable-next-line
          element: (
            <ModalJSXElement key={modal} ref={(r) => (ref = r)} modal={modal} />
          ),
          ref: ref,
        });
      });
    }

    this.states = props.states;
    this.onClickStates = props.onclick;
    this.onDropItems = props.onDropItems;
    this.userInput = props.defaultUserInput;

    this.singleButtonBackground = graphics
      .getInstance()
      .loadImage(
        "singleButton",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.imageSrc = "";
    let image: Maybe<JSX.Element> = this.props.image;
    if (this.states && props.defaultState) {
      const initialState = this.states.find(
        (s) => s.name === props.defaultState
      );
      if (initialState) {
        this.imageSrc = initialState.image;
        image = graphics
          .getInstance()
          .loadImage(
            initialState.image,
            initialState.altText ? initialState.altText : "",
            [],
            this.state.userScale,
            0.766,
            this.state.objectScale
          );
      }
    }

    this.state = {
      position: this.props.position,
      userScale: this.props.userScale,
      sceneScale: this.props.sceneScale,
      objectScale: this.props.objectScale,
      image: image,
      text: this.props.text,
      currentState: props.defaultState,
      userInput: props.defaultUserInput,
      ejectAltText: "",
      showLabel: false,
      hover: "",
    };

    this.buttonsBackground3Buttons = graphics
      .getInstance()
      .loadImage(
        "hover_menu",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.buttonsBackground2Buttons = graphics
      .getInstance()
      .loadImage(
        "hover_2buttons",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.ejectButton = graphics
      .getInstance()
      .loadImage(
        "eject_button",
        this.state.ejectAltText || "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.optionsButton = graphics
      .getInstance()
      .loadImage(
        "options_button",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );

    this.glossaryButton = graphics
      .getInstance()
      .loadImage(
        "glossary_button",
        "",
        [],
        this.state.userScale,
        this.state.sceneScale
      );
  }

  componentDidMount(): void {
    SimManager.getInstance().registerInteractableObject(this);
  }

  setItemState = (postState: string) => {
    const changeState = this.states.filter(
      (x: { name: string }) => x.name === postState
    );
    if (changeState.length > 0) {
      SimManager.getInstance().updateAltTextFunction(this.props.id);
      const image = graphics
        .getInstance()
        .loadImage(
          changeState[0].image,
          changeState[0].altText ? changeState[0].altText : "",
          [],
          this.state.userScale,
          0.766,
          this.state.objectScale
        );
      this.setState(
        {
          image: image,
          currentState: postState,
          ejectAltText: changeState[0].ejectAltText
            ? changeState[0].ejectAltText
            : "",
        },
        SimManager.getInstance().handleUpdateItems
      );
      this.imageSrc = changeState[0].image;

      if (changeState[0].ejectAltText)
        this.ejectButton = graphics
          .getInstance()
          .loadImage(
            "eject_button",
            this.state.ejectAltText || "",
            [],
            this.state.userScale,
            this.state.sceneScale
          );
      return true;
    }
    return false;
  };

  changeText(text: string, name?: string) {
    if (this.state.text) {
      if (name) {
        const updatedText = this.state.text.map((textItem: any) => {
          if (textItem.name === name || textItem.id === name) {
            return { ...textItem, text: text };
          }
          return textItem;
        });
        this.setState({ text: updatedText });
      } else {
        this.setState({
          text: [
            {
              name: this.state.text[0].id,
              position: this.state.text[0].position,
              style: this.state.text[0].style,
              text: text,
            },
          ],
        });
      }
    }
  }

  updateLabel(label: Label) {
    const updatedStates = this.states.map((state) => ({
      ...state,
      label: label,
    }));

    this.states = updatedStates;
  }

  getText(): string {
    if (this.state.text) return this.state.text[0].text;
    else return "";
  }

  onItemDropped = (item: ControllableObject | CustomObject) => {
    this.onDropItems.forEach((element: any) => {
      if (element.item == "Self") {
        if (
          element.droppedItemName.includes(item.getId()) &&
          (this.state.currentState == element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(this.state.currentState))))
        ) {
          let conditionsChecked = false;
          if (!element.conditions) {
            this.setItemState(
              (
                element.transitions?.find(
                  (x: { initialState: string }) =>
                    x.initialState === this.state.currentState
                ) || element
              ).postState
            );
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          } else {
            element.conditions.forEach((condition: Condition) => {
              // if (condition.conditionItem == "Dropped")
              const itemId =
                condition.conditionItem === "Dropped"
                  ? item.getId()
                  : this.getId();

              if (
                SimManager.getInstance().checkCondition({
                  ...condition,
                  conditionItem: itemId,
                })
              ) {
                conditionsChecked = true;
              }
            });
          }

          if (conditionsChecked) {
            this.setItemState(
              (
                element.transitions?.find(
                  (x: { initialState: string }) =>
                    x.initialState === this.state.currentState
                ) || element
              ).postState
            );
            this.setState({
              hover: "buttons",
              isHovered: true,
            });
            SimManager.dispatchAction(element.actionName, {
              id: this.getId(),
              droppedId: item.getId(),
            });
            element.message &&
              SimManager.getInstance().addToast(
                element.message.title,
                element.message.message,
                element.message.type
              );
          }
        }
      } else if (element.item == "Dropped") {
        if (element.droppedItemName.includes(item.getId())) {
          if (
            item.getCurrentState() === element.initialState ||
            (!element.initialState &&
              (!element.initialStates ||
                element.initialStates?.includes(item.getCurrentState())))
          ) {
            let conditionsChecked = false;
            if (!element.conditions) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: { initialState: string }) =>
                      x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            } else {
              element.conditions.forEach((condition: Condition) => {
                const itemId =
                  condition.conditionItem === "Dropped"
                    ? item.getId()
                    : this.getId();

                if (
                  SimManager.getInstance().checkCondition({
                    ...condition,
                    conditionItem: itemId,
                  })
                )
                  conditionsChecked = true;
              });
            }

            if (conditionsChecked) {
              SimManager.updateObjectState(
                item.getId(),
                (
                  element.transitions?.find(
                    (x: { initialState: string }) =>
                      x.initialState === item.getCurrentState()
                  ) || element
                ).postState,
                undefined,
                element.resetPosition
              );
              SimManager.dispatchAction(element.actionName, {
                id: this.getId(),
                droppedId: item.getId(),
              });
              element.message &&
                SimManager.getInstance().addToast(
                  element.message.title,
                  element.message.message,
                  element.message.type
                );
            }
          }
        }
      }
    });
  };

  getCurrentState = () => {
    return this.state.currentState;
  };

  getButtons = () => {
    const currentState = this.states.find(
      (state: { name: string }) => state.name === this.state.currentState
    );
    if (currentState && currentState.buttons) {
      return [
        ...(this.props.buttons ? this.props.buttons : []),
        ...currentState.buttons,
      ];
    }
    return this.props.buttons;
  };

  getLabel = () => {
    return (
      this.states.find((state) => state.name === this.state.currentState)
        ?.label || this.props.label
    );
  };

  getAriaLabel = () => {
    const label = this.getLabel();
    if (!label) {
      return this.props.id;
    }
    if (typeof label === "string") {
      return label;
    }
    if (typeof label === "object" && "text" in label) {
      return label.text;
    }
    if (
      typeof label === "object" &&
      "topLine" in label &&
      "bottomLine" in label
    ) {
      return `${label.topLine.text} ${label.bottomLine.text}`;
    }
    return this.props.id;
  };

  setItemPosition(position?: Vector3, reset = false) {
    this.setState({
      position: reset ? this.props.position : position || this.state.position,
    });
  }

  getItemPosition(): Vector3 {
    return this.state.position;
  }

  updateUserInput = (newValuePair: UserInput) => {
    this.userInput = { ...this.state.userInput, ...newValuePair };
    this.setState({
      userInput: this.userInput,
    });

    SimManager.getInstance().handleUpdateItems();
  };

  getId(): string {
    return this.props.id;
  }

  getName(): string {
    return this.props.name;
  }

  getCurrentInput = () => {
    return this.userInput;
  };

  setScale(userScale: number, sceneScale: number) {
    this.setState({ userScale: userScale, sceneScale: sceneScale });
  }

  updateAltText = (text: string) => {
    const image = graphics
      .getInstance()
      .loadImage(
        this.imageSrc,
        text,
        [],
        this.state.userScale,
        0.766,
        this.state.objectScale
      );

    if (image) this.setState({ image: image });
  };

  render() {
    const CSSStyle: React.CSSProperties = {
      position: "absolute",
      cursor: "default",
      userSelect: "none",
      left: calculateXOffset(this.state.position.x),
      top: calculateYOffset(this.state.position.y),
      zIndex: this.state.position.z,
      scale: `calc(${this.state.objectScale || 1})`,
    };

    let textStyle: CSSProperties = { display: "none" };
    let text = "";

    //If text is present, display it on the interactable object
    if (this.state.text) {
      text = this.state.text[0].text;

      const properties = this.state.text[0].style.split(";").filter(Boolean);

      const styleObject: React.CSSProperties = {
        left: calculateXOffset(this.state.position.x, {
          offset: this.state.text[0].position.x,
        }),
        top: calculateYOffset(this.state.position.y, {
          offset: this.state.text[0].position.y,
        }),
        zIndex: this.state.text[0].position.z,
        position: "absolute",
        scale: `calc(${this.state.objectScale || 1})`,
      };

      properties.forEach(
        (property: {
          split: (arg0: string) => {
            (): any;
            new (): any;
            map: {
              (arg0: (item: any) => any): [any, any];
              new (): any;
            };
          };
        }) => {
          const [key, value] = property.split(":").map((item) => item.trim());
          if (key && value) {
            // Convert CSS property name to camelCase
            const camelCaseKey = key.replace(/-([a-z])/g, (g: string[]) =>
              g[1].toUpperCase()
            );
            // Determine if the value should be converted to number

            const numericValue = parseFloat(value);
            const formattedValue = isNaN(numericValue) ? value : numericValue;
            styleObject[camelCaseKey as keyof CSSProperties] = formattedValue;
          }
        }
      );

      // @ts-expect-error: Setting css scene-scale
      styleObject["position" as keyof CSSProperties] = "absolute";
      textStyle = styleObject;
    }

    const handleClickKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClickEvent();
      }
    };

    const onClickEvent = () => {
      //Send event to SimManager
      SimManager.getInstance().customClickFunction(this);

      //Check if Modal needs to open
      if (this.modalName) {
        if (ModalManager.getInstance().openModal(this.modalName))
          SimManager.getInstance().openModal(this.modalName, this.props.name);
      }

      let stateChanged = false;
      //Changing its own state
      for (const element of this.onClickStates) {
        if (
          element.initialState === this.state.currentState &&
          element.item === "Self"
        ) {
          let conditionsChecked = false;
          if (element.conditions) {
            element.conditions.forEach((condition: Condition) => {
              if (!SimManager.getInstance().checkCondition(condition)) {
                conditionsChecked = true;
              }
            });
          }

          if (!conditionsChecked && !stateChanged) {
            this.setItemState(element.postState);
            stateChanged = true;
          }
        }
        //Changing state of other items
        else if (
          element.initialState ==
            SimManager.getInstance().getState(element.item) &&
          element.item != "Self"
        ) {
          let conditionsChecked = false;
          if (element.conditions) {
            element.conditions.forEach((condition: Condition) => {
              if (SimManager.getInstance().checkCondition(condition)) {
                conditionsChecked = true;
              }
            });
          }
          if (!element.conditions || conditionsChecked)
            SimManager.getInstance().updateState(
              element.item,
              element.postState,
              element.position
            );
        }
      }
    };

    // eslint-disable-next-line
    let textDiv: JSX.Element = <></>;

    function containsHTML(str: string) {
      const htmlTagRegex = /<[^>]+>/g;
      return htmlTagRegex.test(str);
    }

    if (this.state.text && this.state.currentState !== "hidden") {
      textDiv = (
        <div style={{ ...textStyle, pointerEvents: "none" }}>
          {containsHTML(text) ? (
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{ p: React.Fragment }}
            >
              {text.replace("\\_", "_")}
            </ReactMarkdown>
          ) : (
            <MathJax> {text} </MathJax>
          )}
        </div>
      );
    }

    const handleMouseEnter = () => {
      this.setState({
        isHovered: true,
      });
    };

    const handleMouseLeave = () => {
      this.setState({
        isHovered: false,
      });
    };

    const handleFocus = (event: React.FocusEvent) => {
      // Only set hover state if focus is coming from outside the component
      // This prevents DOM updates when focus moves between child buttons
      const isFromChild = event.target !== event.currentTarget;
      if (!isFromChild) {
        this.setState({
          isHovered: true,
        });
      }
    };

    const handleBlur = (event: React.FocusEvent) => {
      // Only clear hover state if focus is leaving the entire component
      // Check if the new focus target is outside this component
      const isLeavingComponent = !event.currentTarget.contains(
        event.relatedTarget
      );

      if (isLeavingComponent) {
        this.setState({
          isHovered: false,
        });
      }
    };

    return (
      <div>
        <div
          id={this.props.id}
          style={CSSStyle}
          className="graphics"
          role="button"
          tabIndex={0}
          aria-label={this.getAriaLabel()}
          data-object-scale={this.state.objectScale}
          data-object-state={this.state.currentState}
          onKeyDown={handleClickKeyDown}
          onClick={onClickEvent}
          onMouseDown={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...(!this.state.image && { tabIndex: -1 })}
        >
          {this.state.image && (
            <InteractableButtons
              scope={this.props.id}
              buttons={this.getButtons()}
              label={this.getLabel()}
              scale={this.state.objectScale}
              visible={this.state.isHovered}
            />
          )}
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {" "}
            {this.state.image}
          </div>
        </div>
        {textDiv}
        {this.modals.map((modal) => modal.element)}
      </div>
    );
  }
}

export default InteractableObject;
